'use client'

import { useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { CollapsibleSection } from './CollapsibleSection'
import { Chip } from './autoDomainChips'
import { apiGet } from './entriesApi'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import {
  BRAND_POSITION_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  VERDICT_LABEL,
  VERDICT_TONE,
  verdictOf,
  type AutoDomainCandidateDetail,
} from './autoDomainTypes'

// The sourcing side of an entry that was promoted out of Domain Candidates
// (raw_entries.source = 'auto_domains'). Everything the entry pages know
// about a report is what the reporter said; here the "reporter" was the
// sourcing run, so this is the equivalent of the original submission: which
// strategy/gram proposed the hostname, how it scored, what the analysis
// found before anyone promoted it, and a way back to the candidate for the
// full evidence (passes, HTTP, screenshots). Keyed off the provenance block
// promote_candidate() writes into raw_entries.original_payload
// (jupyter-fraud libs/utils/db/auto_domains.py::build_promotion_payload),
// then refreshed live from the candidate itself when it can be reached.

export type AutoDomainProvenance = {
  candidate_id: string
  run_id?: string | null
  hostname?: string
  registrable_domain?: string | null
  strategy_code?: string | null
  gram?: string | null
  query_string?: string | null
  quality_score?: number | null
  verdict?: string | null
  verdict_score?: number | null
  brand_position?: string | null
  corroboration_count?: number | null
  is_known?: boolean | null
  domain_id?: string | null
  promoted_by?: string | null
}

export function readAutoDomainProvenance(originalPayload: unknown): AutoDomainProvenance | null {
  const p = (originalPayload as any)?.autoDomainProvenance
  return p && typeof p === 'object' && typeof p.candidate_id === 'string' ? (p as AutoDomainProvenance) : null
}

export function SourcingProvenanceSection({
  provenance,
  entryDomainUrl,
  theme,
  onOpenCandidate,
}: {
  provenance: AutoDomainProvenance
  /** The domain the entry pipeline attached (domain.url) — shown next to the
   * sighted hostname when they differ, which is exactly the brand-in-subdomain
   * case (sighted www.ctt.pt.x.xyz, campaign keyed on x.xyz). */
  entryDomainUrl?: string | null
  theme: ProcessedTheme
  onOpenCandidate?: (candidateId: string) => void
}) {
  const c = watchClasses(theme)
  const [detail, setDetail] = useState<AutoDomainCandidateDetail | null>(null)
  const [gone, setGone] = useState(false)

  useEffect(() => {
    let cancelled = false
    apiGet<AutoDomainCandidateDetail>(`/auto-domains/candidates/${provenance.candidate_id}`).then((r) => {
      if (cancelled) return
      if (r.success && r.data) setDetail(r.data)
      else setGone(true)
    })
    return () => { cancelled = true }
  }, [provenance.candidate_id])

  const cand = detail?.candidate
  const status = cand?.status
  const verdict = cand ? verdictOf(cand) : provenance.verdict ? verdictOf({ verdict: provenance.verdict }) : null
  const verdictScore = cand?.verdict_score ?? provenance.verdict_score
  const hostname = cand?.hostname ?? provenance.hostname ?? '—'
  const strategy = cand?.strategy_code ?? provenance.strategy_code ?? '—'
  const gram = cand?.gram ?? provenance.gram ?? '—'
  const query = cand?.query_string ?? provenance.query_string ?? '—'
  const quality = cand?.quality_score ?? provenance.quality_score
  const brandPos = (cand?.brand_position ?? provenance.brand_position) as keyof typeof BRAND_POSITION_LABEL | undefined
  const passes = detail?.passes ?? []
  const latest = passes[0]
  const browserRows = passes.flatMap((p) => p.rows.filter((r) => r.analysis_layer === 'browser'))
  const shots = browserRows.filter((r) => r.r2_key).length
  const hostDiffers = !!entryDomainUrl && hostname !== entryDomainUrl && hostname !== '—'

  return (
    <CollapsibleSection label="Sourcing" theme={theme} defaultOpen>
      <div className="flex flex-wrap items-center gap-2">
        <Chip label="auto-sourced" tone="sky" title="This entry was promoted out of Domain Candidates — the sourcing run is its reporter" />
        {status && <Chip label={`candidate · ${STATUS_LABEL[status]}`} tone={STATUS_TONE[status]} />}
        {verdict && <Chip label={`${VERDICT_LABEL[verdict]}${verdictScore != null ? ` · ${Number(verdictScore).toFixed(2)}` : ''}`} tone={VERDICT_TONE[verdict]} />}
        {brandPos && brandPos !== 'none' && <Chip label={BRAND_POSITION_LABEL[brandPos]} tone={brandPos === 'sld' ? 'neutral' : 'amber'} />}
        {provenance.is_known && <Chip label="was already flagged when sighted" tone="emerald" />}
        {onOpenCandidate && !gone && (
          <button
            type="button"
            data-action="open-candidate"
            onClick={() => onOpenCandidate(provenance.candidate_id)}
            className={`ml-auto flex items-center gap-1.5 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal hover:border-signal ${c.line} ${c.faint}`}
          >
            <ExternalLink className="h-3 w-3" /> Open candidate · full evidence
          </button>
        )}
      </div>
      <p className={`mt-3 text-[12px] ${c.muted}`}>
        Sighted as <span className={`font-mono ${c.text}`}>{hostname}</span> by strategy <span className={`font-mono ${c.text}`}>{strategy}</span>, gram <span className={`font-mono ${c.text}`}>{gram}</span> (query <span className="font-mono">{query}</span>)
        {quality != null && <> · sourcing quality {Number(quality).toFixed(2)}</>}
        {provenance.corroboration_count != null && provenance.corroboration_count > 1 && <> · reached by {provenance.corroboration_count} grams</>}
        {provenance.promoted_by && <> · promoted by <span className="font-mono">{provenance.promoted_by}</span></>}.
        {hostDiffers && (
          <> The entry pipeline keys campaigns on the registrable domain, so this entry carries <span className={`font-mono ${c.text}`}>{entryDomainUrl}</span>; the sighted hostname (with its subdomain lure) lives on the candidate.</>
        )}
      </p>
      {gone && <p className={`mt-2 font-mono text-[11px] ${c.faint}`}>The candidate row is no longer available (its run was deleted); the values above are the snapshot stored with the entry.</p>}
      {latest && (
        <div className="mt-3">
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>
            Latest analysis pass · {compactDate(latest.createdAt)}{latest.proxyLabel ? ` · via ${latest.proxyLabel}` : ''} · {passes.length} pass{passes.length === 1 ? '' : 'es'}{shots ? ` · ${shots} screenshot capture${shots === 1 ? '' : 's'}` : ''}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[...latest.rows].sort((a, b) => a.analysis_layer.localeCompare(b.analysis_layer)).map((r) => (
              <span key={r.id} className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-mono text-[11px] ${c.line} ${c.muted}`}>
                <span className={`h-2 w-2 rounded-full ${r.is_alive === true ? 'bg-success' : r.is_alive === false ? 'bg-destructive' : 'bg-subtle'}`} />
                {r.analysis_layer.toUpperCase()}/{r.test_type}
                {r.analysis_layer === 'http' && r.raw_result?.status != null && <span className={c.faint}>· HTTP {r.raw_result.status}</span>}
                {r.analysis_layer === 'classify' && r.verdict && <span className={c.faint}>· {VERDICT_LABEL[verdictOf(r)]}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
    </CollapsibleSection>
  )
}
